import * as envUtils from '@/utils/environment';
import * as authModels from '@gik/auth/utils/authModals';
import * as authApi from '@gik/api/users/auth';
import { cookies } from '@gik/core/utils/Cookies';
import { userStoreApi } from '@gik/core/store/UserStore';

type BaseAuthModalSettings = {
  onCancel?: () => Promise<void> | void;
};

export type LoginModalSettings = BaseAuthModalSettings & {
  oauthErrorMessage?: string;
  reloadAOnLogin?: boolean;
};

interface IBaseSignupModalSettings extends BaseAuthModalSettings {}

export enum CalloutBlockType {
  GENERIC = 'generic',
  UPDATES = 'updates',
  CALENDAR = 'calendar',
}

interface IGenericCalloutBlockSignupModalSettings extends IBaseSignupModalSettings {
  calloutBlock?: CalloutBlockType.GENERIC;
}

interface IUpdatesCalloutBlockSignupModalSettings extends IBaseSignupModalSettings {
  calloutBlock: CalloutBlockType.UPDATES;
  recipientName: string;
}

interface ICalendarCalloutBlockSignupModalSettings extends IBaseSignupModalSettings {
  calloutBlock: CalloutBlockType.CALENDAR;
  recipientName: string;
}

export type SignupModalSettings =
  | IGenericCalloutBlockSignupModalSettings
  | IUpdatesCalloutBlockSignupModalSettings
  | ICalendarCalloutBlockSignupModalSettings;

export type AuthModalSettings = LoginModalSettings & SignupModalSettings;

export enum HasUserPreviouslyLoggedInValues {
  FALSE = '0',
  TRUE = '1',
}

export const authCookieKeys = {
  hasUserPreviouslyLoggedIn: 'gik-has-user-previously-logged-in',
  loginErrorCookieName: 'login_error_cookie',
};

/**
 * Most high level function to attempt an automatic user login.
 * The user credentials can come from a config endpoint or from a jwt token etc...
 */
export function autoLogin(userId?: string) {
  // if we have a userId then log the user in with the userStore
  if (userId) {
    userStoreApi.getState().login(userId);
  }

  userStoreApi.getState().setReady();

  // check for oauth errors and show login modal
  // FIXME: modal doesn't launch if there is no delay (dialog manager is not ready?)
  if (window?.location.pathname !== `/create`) {
    setTimeout(() => {
      const loginError = cookies.get(authCookieKeys.loginErrorCookieName);
      if (loginError) {
        login({ oauthErrorMessage: loginError });
        cookies.remove(authCookieKeys.loginErrorCookieName);
      }
    }, 500);
  }
}

/**
 * Most high level function to perform a login sequence.
 *
 * Whether this is a opening a modal or navigating to a specific route, this function should determine the behaviour.
 *
 * @param settings Optional parameter containing settings for error message shown to user on a failed oauth login (e.g.
 * incorrect provider), and what action to take when the flow is explicitly cancelled by the user
 */
export async function login(settings?: AuthModalSettings) {
  const response = await authModels.openLoginModal(settings);
  if (response) {
    performLogin(response.userId, settings?.reloadAOnLogin);
    return response.userId;
  }
}

/**
 * Low level login login sequence
 *
 * @param userId The id of the user to be logged in
 * @param reload Whether to reload the entire page or not
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function performLogin(userId: string, reload = true) {
  cookies.set(authCookieKeys.hasUserPreviouslyLoggedIn, HasUserPreviouslyLoggedInValues.TRUE);

  // TODO: remove below line after SPA migration, as we currently can't re-render the razor code
  if (reload) {
    window.location.reload();
  } else {
    userStoreApi.getState().login(userId);
  }
}

/**
 * Most high level function to perform a reset password sequence.
 *
 * Whether this is a opening a modal or navigating to a specific route, this function should determine the behaviour.
 */
export async function resetPassword() {
  await authModels.openForgotPasswordModal();
}

/**
 * Most high level function to perform a signup sequence.
 *
 * Whether this is a opening a modal or navigating to a specific route, this function should determine the behaviour.
 */
export async function signup(signUpSettings?: AuthModalSettings) {
  const { id } = await authModels.openSignupModal(signUpSettings);

  if (id) {
    performLogin(id);

    // TODO: Currently set by backend through cookie, fires on page reload.
    // after page migration, fire it instantly here, since there is no page refresh needed.
    /*Analytics.fireEvent('User_Registered_GTM', {
      userId: newUserId,
    });*/
  }
}

/**
 * Most high level function to perform a logout sequence.
 *
 * Whether this is a opening a modal or navigating to a specific route, this function should determine the behaviour.
 */
export async function logout(navigate = true) {
  // TODO: remove below line after SPA migration, as we currently can't re-render the razor code
  navigate = true;

  await authApi.logout();

  // log user out from the userStore
  userStoreApi.getState().logout();

  if (navigate) {
    envUtils.navigateTo(window.location.origin);
  }
}

/**
 * Most high level function to determine and perform a login or signup sequence.
 *
 * Whether this is a opening a modal or navigating to a specific route, this function should determine the behaviour.
 */
export async function loginOrSignup(settings?: AuthModalSettings) {
  if (cookies.get(authCookieKeys.hasUserPreviouslyLoggedIn) === HasUserPreviouslyLoggedInValues.TRUE) {
    await login(settings);
  } else {
    await signup(settings);
  }
}
